import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import OptimizedImage from "../components/OptimizedImage";
import { Check, ChevronRight, ChevronDown, ChevronLeft, Snowflake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalamiPage = () => {
  const { t, lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [imageIndexes, setImageIndexes] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/v2/products`);
        setProducts(data);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error("Error fetching products:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleProduct = (idx) => setExpandedProduct(expandedProduct === idx ? null : idx);
  const nextImage = (i, total) => setImageIndexes((p) => ({ ...p, [i]: ((p[i] || 0) + 1) % total }));
  const prevImage = (i, total) => setImageIndexes((p) => ({ ...p, [i]: ((p[i] || 0) - 1 + total) % total }));

  const getName = (p) => lang === "sq" ? p.name_sq : p.name_en;
  const getIngredients = (p) => lang === "sq" ? p.ingredients_sq : p.ingredients_en;
  const getOrigin = (p) => lang === "sq" ? p.origin_sq : p.origin_en;
  const getStorage = (p) => lang === "sq" ? p.storage_sq : p.storage_en;
  const getNotes = (p) => lang === "sq" ? p.notes_sq : p.notes_en;

  return (
    <div className="pt-20" data-testid="salami-page">
      {/* Header */}
      <section className="relative py-32 bg-[#0A0A0A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src="https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/foj3elbc_021A2415.JPG"
            alt=""
            priority
            sizes="100vw"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">{t.nav.salami}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.salami.title}
          </h1>
          <p className="text-xl text-zinc-300 max-w-2xl">{t.salami.subtitle}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xl text-zinc-600 leading-relaxed">{t.salami.intro}</p>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold tracking-tight text-[#0A0A0A] mb-12" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.salami.products.title}
          </h2>

          {loading ? (
            <div className="text-center py-12 text-zinc-500">{t.common.loading}</div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">{t.blog.empty}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product, index) => {
                const images = product.images || [];
                const currentImageIndex = imageIndexes[index] || 0;
                const isFrozen = product.storage_sq?.includes("-18") || product.storage_en?.includes("-18");

                return (
                  <div key={product.id} className="bg-white border border-zinc-200 overflow-hidden" data-testid="salami-product-card">
                    {images.length > 0 ? (
                      <div className="aspect-square bg-zinc-100 relative">
                        <OptimizedImage src={images[currentImageIndex]} alt={getName(product)} sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw" className="w-full h-full object-contain" />
                        {images.length > 1 && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); prevImage(index, images.length); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1 border border-zinc-200">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); nextImage(index, images.length); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1 border border-zinc-200">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                              {images.map((imgUrl, i) => (
                                <span key={imgUrl || i} className={`w-2 h-2 rounded-full ${i === currentImageIndex ? 'bg-[#0A0A0A]' : 'bg-zinc-300'}`} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="aspect-square bg-zinc-100 flex items-center justify-center">
                        <span className="text-zinc-400 text-4xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                          {getName(product).charAt(0)}
                        </span>
                      </div>
                    )}

                    <button onClick={() => toggleProduct(index)} className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-50 transition-colors border-t border-zinc-200">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                          {getName(product)}
                        </h3>
                        {isFrozen && (
                          <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5">
                            <Snowflake className="w-3 h-3" />
                            {lang === "sq" ? "I Ngrirë" : "Frozen"}
                          </span>
                        )}
                      </div>
                      <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${expandedProduct === index ? "rotate-180" : ""}`} />
                    </button>

                    {expandedProduct === index && (
                      <div className="p-4 border-t border-zinc-100 bg-zinc-50">
                        <div className="space-y-4">
                          {getIngredients(product) && (
                            <div>
                              <h4 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-1">
                                {lang === "sq" ? "Përbërësit" : "Ingredients"}
                              </h4>
                              <p className="text-zinc-700 text-sm leading-relaxed">{getIngredients(product)}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4">
                            {getOrigin(product) && (
                              <div>
                                <h4 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-1">{lang === "sq" ? "Origjina" : "Origin"}</h4>
                                <p className="text-zinc-700 text-sm">{getOrigin(product)}</p>
                              </div>
                            )}
                            {getStorage(product) && (
                              <div>
                                <h4 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-1">{lang === "sq" ? "Ruajtja" : "Storage"}</h4>
                                <p className="text-zinc-700 text-sm">{getStorage(product)}</p>
                              </div>
                            )}
                          </div>
                          {getNotes(product) && (
                            <div className="bg-white p-3 border-l-2 border-[#0A0A0A]">
                              <p className="text-zinc-700 text-sm">{getNotes(product)}</p>
                            </div>
                          )}
                          {product.nutrition && (product.nutrition.energy || product.nutrition.protein) && (
                            <div>
                              <h4 className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">
                                {lang === "sq" ? "Vlerat Ushqyese / 100g" : "Nutrition / 100g"}
                              </h4>
                              <div className="bg-white border border-zinc-200 text-sm">
                                {[
                                  ["energy", lang === "sq" ? "Energjia" : "Energy"],
                                  ["fat", lang === "sq" ? "Yndyrna" : "Fat"],
                                  ["protein", lang === "sq" ? "Proteina" : "Protein"],
                                  ["salt", lang === "sq" ? "Kripë" : "Salt"],
                                ].filter(([k]) => product.nutrition[k]).map(([k, lbl], i, arr) => (
                                  <div key={k} className={`grid grid-cols-2 px-3 py-2 ${i < arr.length - 1 ? "border-b border-zinc-100" : ""}`}>
                                    <span className="text-zinc-600">{lbl}</span>
                                    <span className="text-zinc-800">{product.nutrition[k]}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Quality */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold tracking-tight text-[#0A0A0A] mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.salami.quality.title}
              </h2>
              <ul className="space-y-4">
                {t.salami.quality.points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#0A0A0A] mt-1 flex-shrink-0" />
                    <span className="text-zinc-700 text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-[400px] lg:h-[500px] bg-zinc-900 overflow-hidden">
              <OptimizedImage
                src="https://customer-assets.emergentagent.com/job_meat-chain-pro/artifacts/7hzuv916_021A2373.JPG"
                alt="Tamari deli quality"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t.salami.cta}
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-xl mx-auto">{t.contact.subtitle}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 font-medium hover:bg-zinc-100 transition-colors">
            {t.hero.cta} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SalamiPage;
